import { Box, Typography } from '@oresundsbron/bridge-ui';
import {
  useForm,
  useSubscribe,
  ValidationResult,
} from '@oresundsbron/use-form';
import { tapO } from '@oresundsbron/utilities';
import { paymentAgreementParser } from '@oresundsbron/validators';
import { pipe } from 'fp-ts/lib/function';
import { alt, fromPredicate, getOrElse, map, chain } from 'fp-ts/lib/Option';
import {
  chain as chainTO,
  map as mapTO,
  fromTask,
  getOrElseW as getOrElseWTO,
  fromOption,
} from 'fp-ts/TaskOption';
import { of } from 'fp-ts/Task';
import { Trans, useTranslation } from 'next-i18next';
import { FC, useCallback, useEffect, useMemo, useRef } from 'react';
import {
  PaymentContract,
  Data,
} from '../../../components/Forms/PaymentContract';
import { useCountries } from '../../../hooks/useCountries';
import { AgreementType, useValidateBizz } from '../../../hooks/useValidateBizz';
import { getDefaultCountry } from '../../../lib/countries';
import { pick } from '../../../lib/data';
import { validateT } from '../../../lib/zod';
import {
  addOresundPayBusiness,
  removeOresundPayBusiness,
} from '../../../stores/contracts';
import { chainValidations, isFieldErrorEmpty } from '../../../lib/errors';
import { useRouter } from 'next/router';
import { SignUpFormPart } from '../utils';

const pickBizz = pick<Data>()('bizzNumber');

export const PaymentAgreement: FC<SignUpFormPart & { contract: string }> = ({
  namespace,
  titleTranslationKey,
  contract,
  ns,
}) => {
  const last = useRef(false);
  const { t } = useTranslation(ns, {
    nsMode: 'fallback',
  });
  const countries = useCountries();
  const { locale } = useRouter();
  const validateBizz = useValidateBizz();

  const { skipStep, undoSkipStep } = useForm({ namespace });

  const [oresundPay] = useSubscribe<Data>(namespace, 'oresundsPay');

  useEffect(
    () =>
      pipe(
        oresundPay,
        fromPredicate((x) => x === 'true'),
        tapO(() => {
          undoSkipStep('paymentMethod');
          undoSkipStep('payment');
          if (last.current) {
            return;
          }

          last.current = true;
          addOresundPayBusiness();
        }),
        alt(() =>
          pipe(
            oresundPay,
            fromPredicate((x) => x === 'false'),
            tapO(() => {
              skipStep('paymentMethod');
              skipStep('payment');
              if (!last.current) {
                return;
              }

              last.current = false;
              removeOresundPayBusiness();
            })
          )
        ),
        map(() => {}),
        getOrElse(() => {})
      ),
    [oresundPay, skipStep, undoSkipStep]
  );

  const providerChoices = useMemo(
    () => [
      {
        label: 'ØresundPAY',
        value: 'oresundpay',
      },
      {
        label: 'Brobizz',
        value: 'brobizz',
      },
      {
        label: 'DKV',
        value: 'dkv',
      },
      {
        label: 'Flytpass',
        value: 'flytpass',
      },
      {
        label: 'AS24/Total',
        value: 'as24total',
      },
      {
        label: 'Axxes',
        value: 'axxes',
      },
      {
        label: 'Skyttelpass',
        value: 'skyttelpass',
      },
      {
        label: 'Toll4Europe',
        value: 'toll4europe',
      },
      {
        label: 'Tollticket',
        value: 'tollticket',
      },
      {
        label: 'Eurowag',
        value: 'eurowag',
      },
      {
        label: 'Partners Brobizz',
        value: 'partners_brobizz',
      },
      {
        label: 'AutoSync AS',
        value: 'fremtind',
      },
    ],
    []
  );

  const pickAndValidateBizz = useCallback(
    (data: Data) => (errors?: ValidationResult<Data>) =>
      pipe(
        errors,
        fromPredicate(isFieldErrorEmpty('bizzNumber')),
        map(() => data),
        chain(fromPredicate((x) => x.oresundsPay === 'false')),
        map(pickBizz),
        chain(fromPredicate((x) => !!x.bizzNumber)),
        fromOption,
        mapTO(({ bizzNumber }) => ({
          serialNo: bizzNumber,
          agreementType: contract as AgreementType,
        })),
        chainTO(
          validateBizz<Data>({
            bizzNumber: t('form.validations.bizzNumber.exist'),
          })
        )
      ),
    [contract, t, validateBizz]
  );

  const onValidate = useCallback(
    (data: Data) =>
      pipe(
        data,
        validateT(paymentAgreementParser.spa, t),
        fromTask,
        chainValidations(pickAndValidateBizz(data)),
        getOrElseWTO(() => of(undefined))
      )(),
    [t, pickAndValidateBizz]
  );

  return (
    <PaymentContract
      namespace={namespace}
      title={t(titleTranslationKey)}
      description={t('form.sections.paymentAgreement.description')}
      onValidate={onValidate}
      countryOptions={countries}
      defaultValues={{
        country: getDefaultCountry(locale || ''),
      }}
      payText={
        <Trans
          i18nKey="form.sections.paymentAgreement.agreements.oresundspay.description"
          ns={ns}
        >
          <Typography className="mb-4" />
          <Typography />
        </Trans>
      }
      providerChoices={providerChoices}
      disclaimers={{
        oresundpay: t('form.sections.paymentAgreement.disclaimers.oresundpay'),
        brobizz: (
          <Trans
            i18nKey="form.sections.paymentAgreement.disclaimers.brobizz"
            ns={ns}
          >
            <Box
              as="a"
              href={t('form.sections.paymentAgreement.disclaimers.brobizzLink')}
              className="text-primary-600 underline"
            />
          </Trans>
        ),
      }}
      labels={{
        paymentContract: t('form.labels.paymentContract'),
        paymentProvider: t('form.labels.paymentProvider'),
        bizzNumber: t('form.labels.bizzNumber'),
        licensePlate: t('form.labels.licensePlate'),
        country: t('form.labels.country'),
        loading: t('action.loading'),
        pay: t('form.sections.paymentAgreement.agreements.oresundspay.label'),
        other: t('form.sections.paymentAgreement.agreements.existing.label'),
      }}
      placeholders={{
        country: t('form.placeholders.country'),
        paymentProvider: t('form.placeholders.paymentProvider'),
      }}
    />
  );
};
